import { ApiUrlHelper, IApiUrl, IGuidValue } from '../../utilities/url/ApiUrlHelper';
import { ItemUrlHelper, IItemUrlParts } from '../../utilities/url/ItemUrlHelper';
import { ISharingContextInformation } from '../../interfaces/sharing/SharingInterfaces';
import * as Path from '../../utilities/path/Path';
import { ISPListItem } from '../item/spListItemProcessor/ISPListItemData';

export interface ISharingDataSourceHelperParams {
    itemUrlHelper: ItemUrlHelper;
    sharingContextInformation?: ISharingContextInformation;
    isItemUrlEncoded?: boolean;
}

export default class SharingDataSourceHelper {
    private _itemUrlHelper: ItemUrlHelper;
    private _sharingContextInformation: ISharingContextInformation;

    constructor(params?: ISharingDataSourceHelperParams) {
        this._itemUrlHelper = params.itemUrlHelper;
        this._sharingContextInformation = params.sharingContextInformation;
    }

    public getApiRoot(apiUrlHelper: ApiUrlHelper, item: ISPListItem, parentListId: string): IApiUrl {
        // If this._sharingContextInformation exists, use that object to build API root.
        if (this._sharingContextInformation) {
            // If host passed listUrl + serverRelativeItemUrl, use those values to build root.
            // Otherwise, they (i.e. sync client) must've passed a resource ID so use that.
            if (this._sharingContextInformation.uniqueId) {
                const apiName = this._sharingContextInformation.isFolder ? 'GetFolderById' : 'GetFileById';
                const guidParam: IGuidValue = { guid: this._sharingContextInformation.uniqueId };

                return apiUrlHelper.build()
                    .webByUrl({
                        webUrl: this._sharingContextInformation.webAbsoluteUrl
                    })
                    .method(apiName, guidParam)
                    .segment('ListItemAllFields');
            } else if (this._sharingContextInformation.listUrl) {
                return apiUrlHelper.build()
                    .webByUrl({
                        listUrl: this._sharingContextInformation.listUrl
                    })
                    .method('GetListItem', this._sharingContextInformation.serverRelativeItemUrl);
            } else if (this._sharingContextInformation.itemUrl) {
                return apiUrlHelper.build()
                    .webByUrl({
                        webUrl: this._sharingContextInformation.webAbsoluteUrl
                    })
                    .method('GetFileByUrl', Path.canonicalizeUrl(this._sharingContextInformation.itemUrl))
                    .segment('ListItemAllFields');
            } else if (this._sharingContextInformation.listId && this._sharingContextInformation.listItemId) {
                return apiUrlHelper.build()
                    .webByUrl({
                        webUrl: this._sharingContextInformation.webAbsoluteUrl
                    })
                    .method('Lists', this._sharingContextInformation.listId)
                    .method('GetItemById', this._sharingContextInformation.listItemId);
            } else { /* this._sharingContextInformation.webAbsoluteUrl */
                return apiUrlHelper.build()
                    .webByUrl({
                        webUrl: this._sharingContextInformation.webAbsoluteUrl
                    })
                    .method('GetListItemByResourceId', this._sharingContextInformation.resourceId);
            }
        }

        let itemUrlParts: IItemUrlParts;

        if (item.properties.path) {
            itemUrlParts = this._itemUrlHelper.getUrlParts({
                path: item.properties.path
            });
        } else {
            itemUrlParts = this._itemUrlHelper.getItemUrlParts(item.key);
        }

        let apiUrl = apiUrlHelper.build()
            .webByItemUrl(itemUrlParts)
            .method('Lists', parentListId);

        // Search results don't have ID, so use uniqueId.
        if (item.properties.ID) {
            apiUrl = apiUrl.method('GetItemById', item.properties.ID);
        } else {
            apiUrl = apiUrl.method('GetItemByUniqueId', item.properties.uniqueId);
        }

        return apiUrl;
    }

    public getFileUrl(item: ISPListItem): string {
        return this._itemUrlHelper.getItemUrlParts(item.key).fullItemUrl;
    }

    /**
     * Ensures that the item has the required properties necessary to use
     * the securable sharing APIs.
     * @param item The item that's being shared.
     */
    public canUseSecurable(item: ISPListItem): boolean {
        const properties = item.properties;

        // Obviously can't use if we have no properties.
        if (!properties) {
            return false;
        }

        // Need ID or unique ID to identify the item.
        let hasRequiredProperties = !!properties.ID || !!properties.uniqueId;

        return hasRequiredProperties;
    }
}
